import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronDownIcon,
  DotsHorizontalIcon,
  Trash02Icon,
} from '@langchain/untitled-ui-icons';
import { Alert, Box, LinearProgress } from '@mui/joy';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { Link, useNavigate } from 'react-router-dom';

import { DatasetCrudPane } from '@/components/DatasetCrudPane/DatasetCrudPane';
import { DeleteModal } from '@/components/Delete';
import { useExampleCrud } from '@/components/ExampleCrudPane/hooks';
import { getDataType } from '@/components/ExampleCrudPane/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import { DatasetSelectorPopoverContent } from '@/components/RunsTable/components/DatasetSelectorPopoverContent';
import { TextOverflowTooltip } from '@/components/TextOverflowTooltip';
import useToast from '@/components/Toast/useToast';
import { Button, IconButton, Text, Tooltip } from '@/design-system';
import { usePermissions } from '@/hooks/usePermissions';
import {
  useOrganizationId,
  useSelectedTenant,
  useSession,
  useUpdateAnnotationQueueMutation,
} from '@/hooks/useSwr';
import { DatasetSchema, RunSchema } from '@/types/schema';
import { evaluateIfRunCanBeAddedToDataset } from '@/utils/compare-schemas';
import {
  apiAnnotationQueuesPath,
  appDatasetsPath,
  appOrganizationPath,
  appSessionPath,
} from '@/utils/constants';
import { cn } from '@/utils/tailwind';
import { useLocalStorageState } from '@/utils/use-local-storage-state';

import { AnnotationQueueEmptyState } from '.';
import CommandKeyIcon from '../../icons/CommandKeyIcon.svg?react';
import CornerDownLeftIcon from '../../icons/CornerDownLeftIcon.svg?react';
import { useScrollParent } from '../../utils/VirtuosoCustomScrollParentContext/useScrollParent';
import { KeyboardShortcut } from '../KeyboardShortcutsSidePane';
import Attachments from '../Run/components/Attachments';
import { RunInputsAndOutputs } from '../Run/components/RunInputsAndOutputs';
import {
  AlignQueueHeader,
  AnnotationQueueHeader,
} from './AnnotationQueueHeader';
import { ConfirmationModal } from './ConfirmationModal';
import { AnnotationQueueRubricForm } from './components/AnnotationQueueRubricForm';
import { ATTACHMENT_PREFIX } from './constants';
import { AnnotationQueueInnerProps, AnnotationQueueProps } from './types';
import { useConfirmationModal } from './utils/useConfirmationModal';

export const AnnotationQueue = (props: AnnotationQueueProps) => {
  const datasetDataType = getDataType(undefined, props.currentRun);

  const {
    isConfirmationModalOpen,
    setIsConfirmationModalOpen,
    handleMarkDone,
    handleMoveToEnd,
    handleLeftClick,
    handleRightClick,
    setDontShowConfirmationAgain,
    setHasEdited,
  } = useConfirmationModal(
    props.handleMarkDone,
    props.handleMoveToEnd,
    props.handleLeftClick,
    props.handleRightClick
  );

  const { createToast } = useToast();
  const organizationId = useOrganizationId();

  const { loading: addToDatasetLoading, onSubmit } = useExampleCrud({
    datasetDataType,
    onSuccess: () => {
      const datasetId = props.dataset?.id;
      const title = 'Example added to dataset';
      createToast({
        title,
        description: (
          <Link
            to={`/${appOrganizationPath}/${organizationId}/${appDatasetsPath}/${datasetId}`}
            className="underline"
          >
            View the dataset
          </Link>
        ),
      });
      setHasEdited(false);
    },
    onError: (e) => {
      createToast({
        title: 'Error adding example to dataset',
        description: e,
        error: true,
      });
    },
  });

  return (
    <>
      <ConfirmationModal
        isConfirmationModalOpen={isConfirmationModalOpen}
        setIsConfirmationModalOpen={setIsConfirmationModalOpen}
        setDontShowConfirmationAgain={setDontShowConfirmationAgain}
      />
      <AnnotationQueueInner
        {...props}
        onAddToDataset={onSubmit}
        isAddingToDataset={addToDatasetLoading}
        handleMarkDone={handleMarkDone}
        handleMoveToEnd={handleMoveToEnd}
        handleLeftClick={handleLeftClick}
        handleRightClick={handleRightClick}
        setHasEdited={setHasEdited}
        allowEdits={true}
        hideDatasetSelector={false}
      />
    </>
  );
};

export const AnnotationQueueInner = ({
  currentRun,
  referenceExample,
  annotationQueue,
  handleMarkDone,
  handleMoveToEnd,
  leftDisabled,
  rightDisabled,
  handleLeftClick,
  handleRightClick,
  moveToEndDisabled,
  isDoneLoading,
  isMoveLoading,
  onFeedbackGiven,
  index,
  totalRuns,
  setPeekedRun,
  dataset,
  setDatasetID,
  isDefaultDataset,
  loading,
  mutateSize,
  mutateAnnotationQueue,
  setHasEdited,
  allowEdits,
  hideDatasetSelector,
  renderMarkDoneButton,
  onAddToDataset,
  isAddingToDataset,
  rubricFormRenderer,
}: AnnotationQueueInnerProps) => {
  const { authorize } = usePermissions();
  const [isNewDatasetPaneOpen, setIsNewDatasetPaneOpen] = useState(false);
  const [isDatasetPickerOpen, setIsDatasetPickerOpen] = useState(false);
  const [pageHeight, setPageHeight] = useState('95vh');

  const { createToast } = useToast();
  const organizationId = useOrganizationId();
  const navigate = useNavigate();

  const [inputsObj, setInputsObj] = useState<RunSchema['inputs'] | undefined>(
    currentRun?.inputs
  );
  const [outputsObj, setOutputsObj] = useState<
    RunSchema['outputs'] | undefined
  >(currentRun?.outputs);

  const [s3UrlsObj, setS3UrlsObj] = useState<RunSchema['s3_urls'] | undefined>(
    currentRun?.s3_urls
  );
  const attachments = useMemo(() => {
    if (!currentRun?.s3_urls || !s3UrlsObj) return [];
    return Object.entries(s3UrlsObj)
      .filter(([key]) => key.startsWith(ATTACHMENT_PREFIX))
      .map(([name, urls]) => ({
        name: name.slice(ATTACHMENT_PREFIX.length),
        url: urls.presigned_url,
      }));
  }, [currentRun?.s3_urls, s3UrlsObj]);

  const [rawIO, setRawIO] = useLocalStorageState(
    'ls:raw_io_annotation_queue',
    false
  );

  const [isDeleteRunModalOpen, setIsDeleteRunModalOpen] = useState(false);

  const { data: selectedTenant } = useSelectedTenant();
  const isPersonal = selectedTenant?.is_personal;

  const currentSession = useSession(currentRun?.session_id ?? null);

  const setContainerRef = (node: HTMLDivElement | null) => {
    if (node !== null) {
      setPageHeight(`calc(100vh - ${node.offsetTop}px)`);
    }
  };

  const handleAddToDataset = useCallback(() => {
    if (currentRun?.id && dataset?.id) {
      onAddToDataset({
        inputs: JSON.stringify(inputsObj),
        outputs: JSON.stringify(outputsObj),
        use_source_run_attachments: attachments.map(
          (attachment) => attachment.name
        ),
        source_run_id: currentRun.id,
        dataset_id: dataset?.id,
      });
    }
  }, [
    currentRun?.id,
    dataset?.id,
    inputsObj,
    onAddToDataset,
    outputsObj,
    attachments,
  ]);

  const currentRunWithUpdatedIO = useMemo(() => {
    try {
      return currentRun
        ? {
            id: currentRun?.id,
            inputs: inputsObj,
            outputs: outputsObj,
            extra: currentRun?.extra,
            run_type: currentRun?.run_type,
          }
        : null;
    } catch (e) {
      return null;
    }
  }, [currentRun, inputsObj, outputsObj]);

  const { trigger: updateTrigger } = useUpdateAnnotationQueueMutation({
    queue_id: annotationQueue.id,
  });

  const handleSetDefault = useCallback(
    (e) => {
      const defaultDatasetUpdatedDescriptionPersonalTenant = (
        <div>
          You've set <span className="font-bold">{dataset?.name}</span> as the
          new default dataset for this queue.
        </div>
      );

      const defaultDatasetUpdatedDescription = (
        <div>
          <span className="font-bold">{dataset?.name}</span> is the new default
          dataset for this queue. This updates for your team too. Happy
          collaborating!
        </div>
      );

      e.stopPropagation();
      if (dataset?.id) {
        updateTrigger(
          {
            json: {
              default_dataset: dataset.id,
            },
          },
          {
            onSuccess: () => {
              createToast({
                title: 'Default dataset updated',
                description: isPersonal
                  ? defaultDatasetUpdatedDescriptionPersonalTenant
                  : defaultDatasetUpdatedDescription,
              });
              mutateAnnotationQueue?.();
            },
            onError: (e) => {
              createToast({
                title: 'Error updating default dataset',
                description: e.message,
                error: true,
              });
            },
          }
        );
      }
    },
    [
      createToast,
      dataset?.id,
      dataset?.name,
      isPersonal,
      updateTrigger,
      mutateAnnotationQueue,
    ]
  );

  const handleNewDatasetCreated = useCallback(
    (id: string, newDataset: DatasetSchema) => {
      setHasEdited(false);
      setDatasetID(newDataset.id);
      const toastDescription = (
        <div>
          Successfully created new dataset{' '}
          <Link
            className="font-bold text-brand-green-400 underline"
            to={`/${appOrganizationPath}/${organizationId}/${appDatasetsPath}/${id}`}
          >
            {newDataset?.name}
          </Link>{' '}
          and added the current run.
        </div>
      );
      createToast({
        title: 'New Dataset Created',
        description: toastDescription,
      });
    },
    [createToast, organizationId, setHasEdited, setDatasetID]
  );

  const onClickViewRun = useCallback(() => {
    setPeekedRun(currentRun?.id ?? null);
  }, [setPeekedRun, currentRun?.id]);

  const onClickDeleteRun = useCallback(() => {
    setIsDeleteRunModalOpen(true);
  }, [setIsDeleteRunModalOpen]);

  const onClickViewProject = useCallback(() => {
    navigate(`${appSessionPath}/${currentSession.data?.id}`);
  }, [navigate, currentSession.data?.id]);

  // ------------------------ HOTKEYS ---------------------------- //

  useHotkeys(
    'esc',
    (event) => {
      event.preventDefault();
      if (document.activeElement) {
        (document.activeElement as HTMLElement).blur();
      }
      if (isDatasetPickerOpen) {
        setIsDatasetPickerOpen(false);
      }
    },
    { enableOnContentEditable: true }
  );
  // keyboard shortcut for cmd(mac)/ctrl(win) + enter to click "done"
  useHotkeys('meta+return, ctrl+return', handleMarkDone, {
    enabled: !isNewDatasetPaneOpen,
    enableOnContentEditable: true,
  });
  // keyboard shortcut "v" opens up view run pane
  useHotkeys('v', onClickViewRun, { enabled: !isNewDatasetPaneOpen });
  // keyboard shortcut "p" clicks view project
  useHotkeys('p', onClickViewProject, { enabled: !isNewDatasetPaneOpen });
  // keyboard shortcut "d" adds to dataset if dataset is selected
  useHotkeys(
    'd',
    (event: KeyboardEvent) => {
      event.preventDefault();
      handleAddToDataset();
    },
    { enabled: !isNewDatasetPaneOpen && !isAddingToDataset }
  );
  // j and k for next and previous
  useHotkeys('l', handleRightClick, { enabled: !isNewDatasetPaneOpen });
  useHotkeys('h', handleLeftClick, { enabled: !isNewDatasetPaneOpen });
  // move to end
  useHotkeys('e', handleMoveToEnd, {
    enabled: !isNewDatasetPaneOpen && !moveToEndDisabled && !isMoveLoading,
  });
  useHotkeys('r', () => setRawIO((prev) => !prev), {
    enabled: !isNewDatasetPaneOpen && !loading,
  });

  useEffect(() => {
    if (!loading && !currentRun) {
      mutateSize?.();
    }
  }, [currentRun, loading, mutateSize]);

  const ruleId = annotationQueue?.source_rule_id;

  // ------------------------ END HOTKEYS ---------------------------- //

  const scrollParentRef = useScrollParent();
  const defaultDatasetId = annotationQueue.default_dataset;

  let content: React.ReactNode | null = null;
  if (totalRuns === 0) {
    content = (
      <AnnotationQueueEmptyState
        organizationId={organizationId}
        annotationQueueName={annotationQueue?.name ?? ''}
        annotationQueueId={annotationQueue.id}
        runRuleId={annotationQueue?.run_rule_id}
        hideHeader={true}
      />
    );
  } else {
    content = (
      <>
        <div
          className="flex grow flex-col items-center self-stretch overflow-y-auto overflow-x-visible"
          ref={scrollParentRef}
        >
          <div className="z-0 mb-4 mr-4 flex w-[calc(100%-16px)] max-w-[900px] flex-col items-stretch rounded-xl px-2 py-6">
            {loading ? (
              <LinearProgress />
            ) : currentRun ? (
              <>
                <div className="flex flex-row items-center justify-between">
                  <div className="shrink font-medium display-base">
                    {currentRun.name}
                  </div>
                  <div className="flex items-center gap-3">
                    {currentRun != null && (
                      <>
                        <button
                          type="button"
                          className="inline-flex h-8 shrink-0 items-center justify-start rounded border border-secondary px-2 text-sm hover:bg-secondary"
                          onClick={() => setRawIO((prev) => !prev)}
                        >
                          <span className="my-auto">
                            {rawIO ? 'View Rendered' : 'View Raw'}
                          </span>
                          <KeyboardShortcut keys={['R']} />
                        </button>
                        {authorize('annotation-queues:delete') && (
                          <button
                            type="button"
                            className="inline-flex h-8 items-center justify-start rounded border border-secondary px-2 text-sm hover:bg-secondary"
                            onClick={onClickDeleteRun}
                          >
                            <Trash02Icon className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          type="button"
                          className="inline-flex h-8 shrink-0 items-center justify-start rounded border border-secondary px-2 text-sm hover:bg-secondary"
                          onClick={onClickViewRun}
                        >
                          <span className="my-auto">View Run</span>
                          <KeyboardShortcut keys={['V']} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-4">
                  <RunInputsAndOutputs
                    onEditInputs={
                      !allowEdits
                        ? undefined
                        : (inputs: RunSchema['inputs']) => {
                            setInputsObj(inputs);
                            setHasEdited(true);
                          }
                    }
                    onEditOutputs={
                      !allowEdits
                        ? undefined
                        : (outputs: RunSchema['outputs']) => {
                            setOutputsObj(outputs);
                            setHasEdited(true);
                          }
                    }
                    run={{
                      ...currentRun,
                      inputs: inputsObj,
                      outputs: outputsObj,
                    }}
                    example={referenceExample}
                    isRunLoading={loading}
                    forceRawRender={rawIO ? 'raw' : 'rendered'}
                    showExpandAll
                  />
                  <Attachments
                    attachments={attachments}
                    onDelete={(attachment) => {
                      setS3UrlsObj((s3Urls) => {
                        const newS3Urls = { ...s3Urls };
                        delete newS3Urls[ATTACHMENT_PREFIX + attachment.name];
                        return newS3Urls;
                      });
                    }}
                    className="px-4"
                    gridClassName="max-h-[250px] w-full"
                  />
                </div>
              </>
            ) : (
              <Box className="m-4">
                <Alert variant="outlined" color="danger">
                  404: This run does not exist
                </Alert>
              </Box>
            )}
          </div>
        </div>

        <div
          className={cn(
            'absolute bottom-0 left-0 right-0 -ml-4 flex items-center justify-between overflow-visible overflow-x-auto border-t border-secondary bg-[var(--joy-palette-background-body)] shadow-[0_-10px_15px_-3px_rgba(0,_0,_0,_0.07),_0_-4px_6px_-2px_rgba(0,_0,_0,_0.02)]',
            hideDatasetSelector && 'justify-center',
            'px-2 py-5'
          )}
        >
          {currentRun ? (
            <div
              className={cn('flex gap-2 self-stretch justify-self-center px-2')}
            >
              {!hideDatasetSelector && (
                <>
                  <Popover
                    open={isDatasetPickerOpen}
                    onOpenChange={setIsDatasetPickerOpen}
                  >
                    <PopoverTrigger className="self-stretch" asChild>
                      <Button
                        variant="outlined"
                        color="secondary"
                        size="md"
                        rightDecorator={ChevronDownIcon}
                        className="max-w-[150px]"
                      >
                        <TextOverflowTooltip align="start">
                          {dataset ? dataset.name : 'Select Dataset'}
                        </TextOverflowTooltip>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      className="w-[508px] p-0"
                      avoidCollisions={false}
                      side={'top'}
                    >
                      <DatasetSelectorPopoverContent
                        runTypeAndInputs={{
                          run_type: currentRun.run_type,
                          inputs: currentRun.inputs,
                        }}
                        handleSelectDataset={(dataset) => {
                          setDatasetID(dataset.id);
                          setIsDatasetPickerOpen(false);
                        }}
                        openNewDatasetPane={() => setIsNewDatasetPaneOpen(true)}
                      />
                    </PopoverContent>
                  </Popover>
                  {dataset && !isDefaultDataset && (
                    <IconButton
                      variant="plain"
                      color="secondary"
                      icon={DotsHorizontalIcon}
                      label="Set as default dataset"
                      onClick={handleSetDefault}
                    />
                  )}

                  <Tooltip
                    title={
                      !dataset
                        ? 'Select a dataset first'
                        : !currentRunWithUpdatedIO
                        ? 'Invalid JSON'
                        : null
                    }
                  >
                    <span className="flex flex-col">
                      <Button
                        size="md"
                        variant="outlined"
                        color="secondary"
                        onClick={handleAddToDataset}
                        disabled={
                          isAddingToDataset ||
                          !dataset ||
                          !currentRunWithUpdatedIO
                        }
                        loading={isAddingToDataset}
                      >
                        <Text variant="md">Add to Dataset</Text>
                        <KeyboardShortcut
                          keys={['D']}
                          className={cn(
                            (isAddingToDataset || !dataset) && 'opacity-10'
                          )}
                        />
                      </Button>
                    </span>
                  </Tooltip>
                </>
              )}
              <Button
                disabled={moveToEndDisabled || isMoveLoading}
                variant="outlined"
                color="secondary"
                size="md"
                onClick={handleMoveToEnd}
                loading={isMoveLoading}
              >
                <Text variant="md">Requeue</Text>
                <KeyboardShortcut
                  keys={['E']}
                  className={cn(
                    (moveToEndDisabled || isMoveLoading) && 'invisible'
                  )}
                />
              </Button>
              {renderMarkDoneButton ? (
                renderMarkDoneButton()
              ) : (
                <Button
                  color="primary"
                  size="md"
                  onClick={handleMarkDone}
                  disabled={isDoneLoading}
                  loading={isDoneLoading}
                >
                  <Text variant="md">Done</Text>

                  <KeyboardShortcut
                    keys={[
                      <CommandKeyIcon className="h-3 w-3" />,
                      <CornerDownLeftIcon className="h-3 w-3" />,
                    ]}
                    className={cn(
                      'gap-1 border-brand-subtle bg-brand-secondary py-1 text-brand-primary',

                      isDoneLoading && 'invisible'
                    )}
                    soloClassName="h-3 w-3"
                  />
                </Button>
              )}
            </div>
          ) : (
            <div className="flex min-h-[42px] gap-3 self-stretch justify-self-center px-2" />
          )}
          <div className="absolute right-5 flex shrink-0 gap-2 whitespace-nowrap">
            {index != undefined && totalRuns != undefined && (
              <div className="inline-flex items-center justify-start gap-[5px] rounded py-1">
                <div className="text-center text-sm font-normal leading-[15px]">
                  {`${index + 1} of ${totalRuns}`}
                </div>
              </div>
            )}
            <Button
              variant="outlined"
              size="md"
              color="secondary"
              onClick={handleLeftClick}
              disabled={leftDisabled}
            >
              <ArrowLeftIcon
                className={cn(leftDisabled && 'text-disabled', 'size-5')}
              />
              <KeyboardShortcut
                keys={['H']}
                className={cn(leftDisabled && 'text-disabled', 'ml-0')}
              />
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              size="md"
              onClick={handleRightClick}
              disabled={rightDisabled}
              className="pl-0"
            >
              <KeyboardShortcut
                keys={['L']}
                className={cn(rightDisabled && 'text-disabled')}
              />
              <ArrowRightIcon
                className={cn(rightDisabled && 'text-disabled', 'size-5')}
              />
            </Button>
          </div>
        </div>
      </>
    );
  }

  return (
    <div
      className="relative bottom-0 -mb-6 -mt-3 flex w-full items-stretch"
      style={{ height: pageHeight }}
      ref={setContainerRef}
    >
      <div className="relative flex grow flex-col items-stretch self-stretch pb-[90px] ">
        {annotationQueue.run_rule_id ? (
          <AlignQueueHeader
            annotationQueueName={annotationQueue.name}
            annotationQueueId={annotationQueue.id}
            organizationId={organizationId}
            index={index}
            totalRuns={totalRuns}
            populatingInProgress={!!ruleId}
            runRuleId={annotationQueue.run_rule_id}
            defaultDatasetId={defaultDatasetId}
          />
        ) : (
          <AnnotationQueueHeader
            annotationQueueName={annotationQueue.name}
            annotationQueueId={annotationQueue.id}
            organizationId={organizationId}
            index={index}
            totalRuns={totalRuns}
            populatingInProgress={!!ruleId}
          />
        )}
        {content}
      </div>

      {(totalRuns ?? 0) > 0 && (
        <div className="-mr-4 w-[450px] min-w-[450px] max-w-[450px] overflow-auto border-l border-secondary bg-white dark:bg-black">
          {currentRun ? (
            rubricFormRenderer ? (
              rubricFormRenderer({
                annotationQueue,
                runId: currentRun.id,
                sessionId: currentRun.session_id,
                onFeedbackGiven,
              })
            ) : (
              <AnnotationQueueRubricForm
                annotationQueue={annotationQueue}
                runId={currentRun.id}
                sessionId={currentRun.session_id}
                onFeedbackGiven={onFeedbackGiven}
              />
            )
          ) : (
            <LinearProgress />
          )}
        </div>
      )}
      {currentRun && (
        <DatasetCrudPane
          onSuccess={handleNewDatasetCreated}
          onCancel={() => setIsNewDatasetPaneOpen(false)}
          label={'New dataset'}
          open={isNewDatasetPaneOpen}
          isChatEnabled={evaluateIfRunCanBeAddedToDataset(currentRun, 'chat')}
          isKVEnabled={evaluateIfRunCanBeAddedToDataset(currentRun, 'kv')}
          isLLMEnabled={evaluateIfRunCanBeAddedToDataset(currentRun, 'llm')}
          preselectedRuns={
            currentRunWithUpdatedIO ? [currentRunWithUpdatedIO] : []
          }
          baseDatasetOnRunType={currentRun?.run_type}
          isManualIO
        />
      )}
      {isDeleteRunModalOpen && currentRun?.id && (
        <DeleteModal
          isOpen={isDeleteRunModalOpen}
          doClose={() => setIsDeleteRunModalOpen(false)}
          endpoint={`${apiAnnotationQueuesPath}/${annotationQueue.id}/runs`}
          id={currentRun?.id}
          modalText="This run will be deleted from the queue for all workspace members. Are you sure?"
          invalidationPrefixes={[apiAnnotationQueuesPath]}
        />
      )}
    </div>
  );
};
